import React, { useEffect } from 'react';
import { useBatteryStore } from '../../store/batteryStore';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Separator } from '../ui/separator';
import { Switch } from '../ui/switch';
import { Loader2, Zap, Battery, Layers, Grid3X3, Maximize, Cpu } from 'lucide-react';

export const ConfigPanel = () => {
  const {
    cells,
    selectedCellId,
    bmsModels,
    selectedBmsId,
    includeBms,
    config,
    constraints,
    isGenerating,
    generationTime,
    results,
    fetchCells,
    fetchBmsModels,
    setSelectedCell,
    setSelectedBms,
    setIncludeBms,
    updateConfig,
    updateConstraints,
    generatePacks,
  } = useBatteryStore();
  
  useEffect(() => {
    fetchCells();
    fetchBmsModels();
  }, [fetchCells, fetchBmsModels]);
  
  const selectedCell = cells.find(c => c.id === selectedCellId);
  const selectedBms = bmsModels.find(b => b.id === selectedBmsId);
  
  const handleGenerate = async () => {
    await generatePacks();
  };
  
  return (
    <div className="h-full flex flex-col bg-zinc-900/50 rounded-lg border border-zinc-800" data-testid="config-panel">
      {/* Header */}
      <div className="p-4 border-b border-zinc-800">
        <h2 className="font-heading text-lg font-bold tracking-wide text-zinc-100 flex items-center gap-2">
          <Battery className="w-5 h-5 text-cyan-400" />
          PACK CONFIGURATION
        </h2>
      </div>
      
      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-6">
        {/* Cell Selection */}
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-xs font-heading uppercase tracking-wider text-zinc-500">
            <Zap className="w-3 h-3" />
            Cell Model
          </div>
          <Select value={selectedCellId} onValueChange={setSelectedCell}>
            <SelectTrigger className="w-full bg-zinc-800 border-zinc-700" data-testid="cell-select">
              <SelectValue placeholder="Select cell type" />
            </SelectTrigger>
            <SelectContent className="bg-zinc-800 border-zinc-700 z-[100]">
              {cells.map(cell => (
                <SelectItem key={cell.id} value={cell.id} className="text-zinc-100 cursor-pointer hover:bg-zinc-700">
                  {cell.name} ({cell.capacity_ah}Ah)
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          {selectedCell && (
            <div className="grid grid-cols-2 gap-2 text-xs font-mono bg-zinc-800/50 rounded p-2">
              <div>
                <span className="text-zinc-500">Diameter:</span>
                <span className="text-zinc-300 ml-1">{selectedCell.diameter}mm</span>
              </div>
              <div>
                <span className="text-zinc-500">Height:</span>
                <span className="text-zinc-300 ml-1">{selectedCell.height}mm</span>
              </div>
              <div>
                <span className="text-zinc-500">Voltage:</span>
                <span className="text-zinc-300 ml-1">{selectedCell.nominal_voltage}V</span>
              </div>
              <div>
                <span className="text-zinc-500">Max Discharge:</span>
                <span className="text-zinc-300 ml-1">{selectedCell.max_continuous_discharge}A</span>
              </div>
            </div>
          )}
        </div>
        
        <Separator className="bg-zinc-800" />
        
        {/* Series/Parallel Configuration */}
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-xs font-heading uppercase tracking-wider text-zinc-500">
            <Grid3X3 className="w-3 h-3" />
            Electrical Configuration
          </div>
          
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs text-zinc-400">Series (S)</Label>
              <Input
                type="number"
                min={1}
                max={100}
                value={config.series}
                onChange={(e) => updateConfig({ series: parseInt(e.target.value) || 1 })}
                className="bg-zinc-800 border-zinc-700 font-mono"
                data-testid="series-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-zinc-400">Parallel (P)</Label>
              <Input
                type="number"
                min={1}
                max={100}
                value={config.parallel}
                onChange={(e) => updateConfig({ parallel: parseInt(e.target.value) || 1 })}
                className="bg-zinc-800 border-zinc-700 font-mono"
                data-testid="parallel-input"
              />
            </div>
          </div>
          
          {selectedCell && (
            <div className="bg-zinc-800/50 rounded p-2 text-xs font-mono">
              <div className="flex justify-between">
                <span className="text-zinc-500">Config:</span>
                <span className="text-cyan-400">{config.series}S{config.parallel}P</span>
              </div>
              <div className="flex justify-between">
                <span className="text-zinc-500">Nominal Voltage:</span>
                <span className="text-zinc-300">{(selectedCell.nominal_voltage * config.series).toFixed(1)}V</span>
              </div>
              <div className="flex justify-between">
                <span className="text-zinc-500">Capacity:</span>
                <span className="text-zinc-300">{(selectedCell.capacity_ah * config.parallel).toFixed(1)}Ah</span>
              </div>
              <div className="flex justify-between">
                <span className="text-zinc-500">Energy:</span>
                <span className="text-zinc-300">
                  {(selectedCell.nominal_voltage * config.series * selectedCell.capacity_ah * config.parallel).toFixed(0)}Wh
                </span>
              </div>
            </div>
          )}
        </div>
        
        <Separator className="bg-zinc-800" />
        
        {/* Parablock Configuration */}
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-xs font-heading uppercase tracking-wider text-zinc-500">
            <Layers className="w-3 h-3" />
            Physical Layout
          </div>
          
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs text-zinc-400">Layers</Label>
              <Input
                type="number"
                min={1}
                max={10}
                value={config.layers}
                onChange={(e) => updateConfig({ layers: parseInt(e.target.value) || 1 })}
                className="bg-zinc-800 border-zinc-700 font-mono"
                data-testid="layers-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-zinc-400">Block Rows</Label>
              <Input
                type="number"
                min={1}
                max={20}
                value={config.parablock_rows}
                onChange={(e) => updateConfig({ parablock_rows: parseInt(e.target.value) || 1 })}
                className="bg-zinc-800 border-zinc-700 font-mono"
                data-testid="block-rows-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-zinc-400">Block Cols</Label>
              <Input
                type="number"
                min={1}
                max={20}
                value={config.parablock_cols}
                onChange={(e) => updateConfig({ parablock_cols: parseInt(e.target.value) || 1 })}
                className="bg-zinc-800 border-zinc-700 font-mono"
                data-testid="block-cols-input"
              />
            </div>
          </div>
          
          <div className="space-y-1.5">
            <Label className="text-xs text-zinc-400">Cell Spacing (mm)</Label>
            <Input
              type="number"
              min={0}
              max={10}
              step={0.5}
              value={config.spacing_mm}
              onChange={(e) => updateConfig({ spacing_mm: parseFloat(e.target.value) || 0 })}
              className="bg-zinc-800 border-zinc-700 font-mono"
              data-testid="spacing-input"
            />
          </div>
        </div>
        
        <Separator className="bg-zinc-800" />
        
        {/* BMS Configuration */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-xs font-heading uppercase tracking-wider text-zinc-500">
              <Cpu className="w-3 h-3" />
              Battery Management System
            </div>
            <Switch
              checked={includeBms}
              onCheckedChange={setIncludeBms}
              data-testid="bms-toggle"
            />
          </div>
          
          {includeBms && (
            <>
              <Select value={selectedBmsId} onValueChange={setSelectedBms}>
                <SelectTrigger className="w-full bg-zinc-800 border-zinc-700" data-testid="bms-select">
                  <SelectValue placeholder="Select BMS" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700 z-[100]">
                  {bmsModels.map(bms => (
                    <SelectItem key={bms.id} value={bms.id} className="text-zinc-100 cursor-pointer hover:bg-zinc-700">
                      {bms.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              {selectedBms && (
                <div className="bg-zinc-800/50 rounded p-2 text-xs font-mono space-y-1">
                  <div className="flex justify-between">
                    <span className="text-zinc-500">Max Continuous:</span>
                    <span className="text-cyan-400">{selectedBms.max_continuous_current}A</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-500">Peak Current:</span>
                    <span className="text-cyan-400">{selectedBms.max_peak_current}A</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-500">Series Range:</span>
                    <span className="text-zinc-300">{selectedBms.min_series}S - {selectedBms.max_series}S</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-500">Dimensions:</span>
                    <span className="text-zinc-300">{selectedBms.length_mm}×{selectedBms.width_mm}×{selectedBms.height_mm}mm</span>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
        
        <Separator className="bg-zinc-800" />
        
        {/* Dimension Constraints */}
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-xs font-heading uppercase tracking-wider text-zinc-500">
            <Maximize className="w-3 h-3" />
            Max Dimensions (Optional)
          </div>
          
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs text-zinc-400">Length (mm)</Label>
              <Input
                type="number"
                placeholder="—"
                value={config.max_length_mm || ''}
                onChange={(e) => updateConfig({ max_length_mm: e.target.value ? parseFloat(e.target.value) : null })}
                className="bg-zinc-800 border-zinc-700 font-mono"
                data-testid="max-length-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-zinc-400">Width (mm)</Label>
              <Input
                type="number"
                placeholder="—"
                value={config.max_width_mm || ''}
                onChange={(e) => updateConfig({ max_width_mm: e.target.value ? parseFloat(e.target.value) : null })}
                className="bg-zinc-800 border-zinc-700 font-mono"
                data-testid="max-width-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-zinc-400">Height (mm)</Label>
              <Input
                type="number"
                placeholder="—"
                value={config.max_height_mm || ''}
                onChange={(e) => updateConfig({ max_height_mm: e.target.value ? parseFloat(e.target.value) : null })}
                className="bg-zinc-800 border-zinc-700 font-mono"
                data-testid="max-height-input"
              />
            </div>
          </div>
        </div>
      </div>
      
      {/* Generate Button */}
      <div className="p-4 border-t border-zinc-800 space-y-2">
        <Button
          onClick={handleGenerate}
          disabled={isGenerating}
          className="w-full bg-blue-600 hover:bg-blue-700 text-white font-heading tracking-wide"
          data-testid="generate-btn"
        >
          {isGenerating ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              GENERATING...
            </>
          ) : (
            <>
              <Zap className="w-4 h-4 mr-2" />
              GENERATE PACKS
            </>
          )}
        </Button>
        
        {results.length > 0 && (
          <div className="text-xs text-center font-mono text-zinc-500">
            Generated <span className="text-cyan-400">{results.length}</span> packs in{' '}
            <span className="text-cyan-400">{generationTime.toFixed(1)}ms</span>
          </div>
        )}
      </div>
    </div>
  );
};
